import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserMd,
  faUsers,
  faUserInjured,
  faUserNurse,
  faSearch,
  faChartLine,
  faCalendarAlt,
  faFileMedical,
  faBell,
  faCog,
  faPlus,
  faEye,
  faEdit,
  faTrash,
  faDownload,
  faFilter,
  faSort,
  faChevronUp,
  faChevronDown,
  faBuilding,
  faUserCheck,
  faFileImport,
  faCog as faSettings
} from '@fortawesome/free-solid-svg-icons';
import './PracticeAdminDashboard.css';

const PracticeAdminDashboard = () => {
  const { practiceId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { clearBreadcrumbs } = useBreadcrumb();

  // Mock data for practice admin dashboard
  const [dashboardData, setDashboardData] = useState({
    practiceName: 'Healthy Heart Cardiology',
    adminName: 'Dr. Sarah Wilson',
    totalPatients: 45,
    activePatients: 38,
    pendingPatients: 5,
    criticalPatients: 2,
    totalProviders: 3,
    totalCaregivers: 8,
    recentUploads: 12,
    missedUploads: 3,
    abnormalReadings: 8,
    billingPeriod: {
      start: '2024-10-01',
      end: '2024-10-31'
    }
  });

  const [patients, setPatients] = useState([
    {
      id: 1,
      name: 'John Doe',
      age: 65,
      status: 'Active',
      lastReading: '2024-10-13',
      bloodPressure: '140/90',
      heartRate: 72,
      weight: 180,
      nextAppointment: '2024-10-20',
      critical: false,
      provider: 'Dr. Smith',
      caregiver: 'Nurse Johnson'
    },
    {
      id: 2,
      name: 'Jane Smith',
      age: 58,
      status: 'Active',
      lastReading: '2024-10-12',
      bloodPressure: '120/80',
      heartRate: 68,
      weight: 165,
      nextAppointment: '2024-10-18',
      critical: false,
      provider: 'Dr. Wilson',
      caregiver: 'Nurse Davis'
    },
    {
      id: 3,
      name: 'Robert Johnson',
      age: 72,
      status: 'Critical',
      lastReading: '2024-10-13',
      bloodPressure: '180/110',
      heartRate: 95,
      weight: 200,
      nextAppointment: '2024-10-15',
      critical: true,
      provider: 'Dr. Brown',
      caregiver: 'Nurse Wilson'
    }
  ]);

  const [providers, setProviders] = useState([
    {
      id: 1,
      name: 'Dr. John Smith',
      email: 'john.smith@practice.com',
      phone: '+1 (555) 123-4567',
      patients: 15,
      status: 'Active',
      lastLogin: '2024-10-13'
    },
    {
      id: 2,
      name: 'Dr. Sarah Wilson',
      email: 'sarah.wilson@practice.com',
      phone: '+1 (555) 234-5678',
      patients: 18,
      status: 'Active',
      lastLogin: '2024-10-12'
    },
    {
      id: 3,
      name: 'Dr. Michael Brown',
      email: 'michael.brown@practice.com',
      phone: '+1 (555) 345-6789',
      patients: 12,
      status: 'Inactive',
      lastLogin: '2024-10-10'
    }
  ]);

  const [caregivers, setCaregivers] = useState([
    {
      id: 1,
      name: 'Nurse Johnson',
      email: 'nurse.johnson@practice.com',
      phone: '+1 (555) 456-7890',
      patients: 8,
      status: 'Active',
      lastLogin: '2024-10-13'
    },
    {
      id: 2,
      name: 'Nurse Davis',
      email: 'nurse.davis@practice.com',
      phone: '+1 (555) 567-8901',
      patients: 10,
      status: 'Active',
      lastLogin: '2024-10-12'
    },
    {
      id: 3,
      name: 'Nurse Wilson',
      email: 'nurse.wilson@practice.com',
      phone: '+1 (555) 678-9012',
      patients: 6,
      status: 'Active',
      lastLogin: '2024-10-11'
    }
  ]);

  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterStatus, setFilterStatus] = useState('all');

  // Update breadcrumbs when component loads
  useEffect(() => {
    clearBreadcrumbs();
  }, []); // Remove updateBreadcrumbs from dependency array

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      'Active': 'status-active',
      'Pending': 'status-pending',
      'Critical': 'status-critical',
      'Inactive': 'status-inactive'
    };
    return statusClasses[status] || 'status-default';
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: faChartLine },
    { id: 'patients', label: 'Patients', icon: faUserInjured },
    { id: 'providers', label: 'Providers', icon: faUserMd },
    { id: 'caregivers', label: 'Caregivers', icon: faUserNurse },
    { id: 'settings', label: 'Settings', icon: faSettings },
    { id: 'reports', label: 'Reports', icon: faDownload }
  ];

  const renderOverviewTab = () => (
    <div className="overview-tab">
      {/* Stats Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="stat-card">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faUserInjured} />
            </div>
            <div className="stat-content">
              <h3>{dashboardData.totalPatients}</h3>
              <p>Total Patients</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="stat-card">
            <div className="stat-icon active">
              <FontAwesomeIcon icon={faUserMd} />
            </div>
            <div className="stat-content">
              <h3>{dashboardData.totalProviders}</h3>
              <p>Providers</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="stat-card">
            <div className="stat-icon pending">
              <FontAwesomeIcon icon={faUserNurse} />
            </div>
            <div className="stat-content">
              <h3>{dashboardData.totalCaregivers}</h3>
              <p>Caregivers</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="stat-card">
            <div className="stat-icon critical">
              <FontAwesomeIcon icon={faBell} />
            </div>
            <div className="stat-content">
              <h3>{dashboardData.criticalPatients}</h3>
              <p>Critical Patients</p>
            </div>
          </div>
        </div>
      </div>

      {/* Practice Management Overview */}
      <div className="row">
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h5>Recent Patient Activity</h5>
            </div>
            <div className="card-body">
              <div className="recent-activity">
                {patients.slice(0, 3).map((patient) => (
                  <div key={patient.id} className="activity-item">
                    <div className="activity-icon">
                      <FontAwesomeIcon icon={faUserInjured} />
                    </div>
                    <div className="activity-details">
                      <h6>{patient.name}</h6>
                      <p>Last Reading: {patient.lastReading} • BP: {patient.bloodPressure}</p>
                      <small>Provider: {patient.provider} • Caregiver: {patient.caregiver}</small>
                    </div>
                    <div className="activity-status">
                      <span className={`status-badge ${getStatusBadge(patient.status)}`}>
                        {patient.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h5>Practice Alerts</h5>
            </div>
            <div className="card-body">
              <div className="practice-alerts">
                <div className="alert-item critical">
                  <div className="alert-icon">
                    <FontAwesomeIcon icon={faBell} />
                  </div>
                  <div className="alert-details">
                    <h6>Critical Patient Alert</h6>
                    <p>Robert Johnson - Blood Pressure: 180/110</p>
                    <small>Provider: Dr. Brown • 1 hour ago</small>
                  </div>
                </div>
                <div className="alert-item warning">
                  <div className="alert-icon">
                    <FontAwesomeIcon icon={faBell} />
                  </div>
                  <div className="alert-details">
                    <h6>Missed Reading Alert</h6>
                    <p>3 patients missed readings today</p>
                    <small>Multiple caregivers • 3 hours ago</small>
                  </div>
                </div>
                <div className="alert-item info">
                  <div className="alert-icon">
                    <FontAwesomeIcon icon={faBell} />
                  </div>
                  <div className="alert-details">
                    <h6>System Update</h6>
                    <p>New features available in dashboard</p>
                    <small>System • 6 hours ago</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPatientsTab = () => (
    <div className="patients-tab">
      {/* Search and Filter */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="search-input-wrapper">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              className="form-control search-input"
              placeholder="Search patients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-3">
          <select
            className="form-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="critical">Critical</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        <div className="col-md-3">
          <button className="btn btn-primary">
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Patient
          </button>
        </div>
      </div>

      {/* Patients Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Patient Name</th>
                  <th>Age</th>
                  <th>Status</th>
                  <th>Provider</th>
                  <th>Caregiver</th>
                  <th>Last Reading</th>
                  <th>Blood Pressure</th>
                  <th>Next Appointment</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {patients.map((patient) => (
                  <tr key={patient.id} className={patient.critical ? 'table-warning' : ''}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="patient-avatar">
                          {patient.name.charAt(0)}
                        </div>
                        <div>
                          <h6 className="mb-0">{patient.name}</h6>
                          {patient.critical && (
                            <small className="text-danger">
                              <FontAwesomeIcon icon={faBell} className="me-1" />
                              Critical
                            </small>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>{patient.age}</td>
                    <td>
                      <span className={`status-badge ${getStatusBadge(patient.status)}`}>
                        {patient.status}
                      </span>
                    </td>
                    <td>
                      <span className="provider-name">{patient.provider}</span>
                    </td>
                    <td>
                      <span className="caregiver-name">{patient.caregiver}</span>
                    </td>
                    <td>{patient.lastReading}</td>
                    <td>
                      <span className={patient.bloodPressure.includes('180') ? 'text-danger fw-bold' : ''}>
                        {patient.bloodPressure}
                      </span>
                    </td>
                    <td>{patient.nextAppointment}</td>
                    <td>
                      <div className="btn-group" role="group">
                        <button className="btn btn-sm btn-outline-primary" title="View Details">
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                        <button className="btn btn-sm btn-outline-secondary" title="Edit">
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button className="btn btn-sm btn-outline-danger" title="Delete">
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProvidersTab = () => (
    <div className="providers-tab">
      {/* Search and Filter */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="search-input-wrapper">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              className="form-control search-input"
              placeholder="Search providers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-3">
          <select
            className="form-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="col-md-3">
          <button className="btn btn-primary">
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Provider
          </button>
        </div>
      </div>

      {/* Providers Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Provider Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Patients</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {providers.map((provider) => (
                  <tr key={provider.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="provider-avatar">
                          <FontAwesomeIcon icon={faUserMd} />
                        </div>
                        <div>
                          <h6 className="mb-0">{provider.name}</h6>
                        </div>
                      </div>
                    </td>
                    <td>{provider.email}</td>
                    <td>{provider.phone}</td>
                    <td>
                      <span className="patient-count">{provider.patients}</span>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusBadge(provider.status)}`}>
                        {provider.status}
                      </span>
                    </td>
                    <td>{provider.lastLogin}</td>
                    <td>
                      <div className="btn-group" role="group">
                        <button className="btn btn-sm btn-outline-primary" title="View Details">
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                        <button className="btn btn-sm btn-outline-secondary" title="Edit">
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button className="btn btn-sm btn-outline-danger" title="Delete">
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCaregiversTab = () => (
    <div className="caregivers-tab">
      {/* Search and Filter */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="search-input-wrapper">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              className="form-control search-input"
              placeholder="Search caregivers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-3">
          <select
            className="form-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="col-md-3">
          <button className="btn btn-primary">
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Caregiver
          </button>
        </div>
      </div>

      {/* Caregivers Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Caregiver Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Patients</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {caregivers.map((caregiver) => (
                  <tr key={caregiver.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="caregiver-avatar">
                          <FontAwesomeIcon icon={faUserNurse} />
                        </div>
                        <div>
                          <h6 className="mb-0">{caregiver.name}</h6>
                        </div>
                      </div>
                    </td>
                    <td>{caregiver.email}</td>
                    <td>{caregiver.phone}</td>
                    <td>
                      <span className="patient-count">{caregiver.patients}</span>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusBadge(caregiver.status)}`}>
                        {caregiver.status}
                      </span>
                    </td>
                    <td>{caregiver.lastLogin}</td>
                    <td>
                      <div className="btn-group" role="group">
                        <button className="btn btn-sm btn-outline-primary" title="View Details">
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                        <button className="btn btn-sm btn-outline-secondary" title="Edit">
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button className="btn btn-sm btn-outline-danger" title="Delete">
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'patients':
        return renderPatientsTab();
      case 'providers':
        return renderProvidersTab();
      case 'caregivers':
        return renderCaregiversTab();
      case 'settings':
        return (
          <div className="settings-tab">
            <h5>Practice Settings</h5>
            <p>Practice configuration and settings will be implemented here.</p>
          </div>
        );
      case 'reports':
        return (
          <div className="reports-tab">
            <h5>Practice Reports</h5>
            <p>Report generation will be implemented here.</p>
          </div>
        );
      default:
        return renderOverviewTab();
    }
  };

  return (
    <div className="practice-admin-dashboard">
      {/* Practice Admin Header */}
      <div className="row m-0 user-header mb-0 card">
        <div className="col-md-12 p-0">
          <div className="d-flex list-unstyled m-0 align-items-center heading-img justify-content-between">
            <h2 className="top-heading">Practice Admin Dashboard</h2>
            <div></div>
            <div className="inner-logo">
              <div className="practice-logo-container">
                <div className="practice-logo">
                  <FontAwesomeIcon icon={faBuilding} className="logo-icon" />
                </div>
                <div className="practice-logo-text">
                  {dashboardData.practiceName.toUpperCase()}
                </div>
              </div>
            </div>
          </div>
          <div className="info_head" style={{ float: 'right' }}>
            <div className="title">Billing Period:</div>
            <span>
              {new Date(dashboardData.billingPeriod.start).toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric'
              })} ~ {new Date(dashboardData.billingPeriod.end).toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric'
              })}
            </span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="card mt-3">
        <div className="card-body p-0">
          <ul className="nav nav-tabs custom-nav-tabs" role="tablist">
            {tabs.map((tab) => (
              <li key={tab.id} className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab.id)}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                >
                  <FontAwesomeIcon icon={tab.icon} className="me-2" />
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content mt-3">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default PracticeAdminDashboard;
